import { Link } from 'react-router-dom';
import { comics, categories } from '@/mocks/comics';
import ComicCard from '@/components/feature/ComicCard';

export default function Home() {
  const featuredComics = comics.filter((c) => c.featured);
  const trendingComics = comics.filter((c) => c.trending);
  const latestComics = [...comics].sort(
    (a, b) => new Date(b.updated).getTime() - new Date(a.updated).getTime()
  );

  const formatViews = (v: number) => {
    if (v >= 10000) return `${(v / 10000).toFixed(1)}万`;
    return v.toString();
  };

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '韩漫网',
    url: 'https://www.hanmanwang.com',
    description: '高质量韩国漫画在线阅读平台，提供免费最新漫画资源',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://www.hanmanwang.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '韩漫网',
    url: 'https://www.hanmanwang.com',
    description: '高质量韩国漫画在线阅读平台',
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '韩漫网提供哪些类型的韩国漫画？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '韩漫网提供丰富多样的韩国漫画类型，包括浪漫爱情、奇幻冒险、热血动作、都市剧情、悬疑惊悚、搞笑日常、古风穿越等多种分类，满足不同读者的阅读喜好。',
        },
      },
      {
        '@type': 'Question',
        name: '韩漫网的漫画资源是否免费？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '是的，韩漫网所有漫画资源均免费提供在线阅读。用户无需付费即可畅享高质量的韩国漫画内容，每日定时更新最新章节。',
        },
      },
      {
        '@type': 'Question',
        name: '韩漫网的更新频率如何？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '韩漫网保持每日更新的频率，确保读者能够第一时间阅读到最新的韩国漫画章节。我们的编辑团队全天候追踪热门漫画的更新动态。',
        },
      },
      {
        '@type': 'Question',
        name: '如何在韩漫网找到想看的漫画？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以通过首页的搜索功能输入漫画名称或作者进行查找，也可以使用分类浏览功能按类型筛选漫画，或查看热门排行和精选推荐来发现优质作品。',
        },
      },
    ],
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>

      <section className="relative w-full h-[520px] md:h-[640px] overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=A%20stylized%20abstract%20artistic%20illustration%20with%20warm%20orange%20and%20cream%20gradients%2C%20soft%20flowing%20shapes%20reminiscent%20of%20comic%20book%20panels%2C%20subtle%20line%20art%20textures%2C%20modern%20minimalist%20design%2C%20elegant%20composition%2C%20no%20people%2C%20no%20text&width=1600&height=900&seq=hero-bg-2026&orientation=landscape"
          alt="韩漫网 - 韩国漫画阅读平台"
          className="absolute inset-0 w-full h-full object-cover object-top"
          width="1600"
          height="900"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/55" />
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4 z-10">
          <h1 className="text-3xl md:text-5xl font-heading font-bold text-background-50 mb-4 tracking-wide">
            探索韩国漫画的无限魅力
          </h1>
          <p className="text-sm md:text-lg text-background-50/85 font-body max-w-2xl mb-8 leading-relaxed">
            海量高质量韩漫资源，每日更新，免费在线阅读
          </p>
          <div className="flex items-center gap-4 flex-wrap justify-center">
            <Link
              to="/category/all"
              className="px-8 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-full text-sm font-label font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              开始阅读
            </Link>
            <Link
              to="/category/all"
              className="px-8 py-3 bg-background-50/20 hover:bg-background-50/30 backdrop-blur-sm text-background-50 border border-background-50/30 rounded-full text-sm font-label transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              浏览分类
            </Link>
          </div>
        </div>
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10">
          <i className="ri-arrow-down-line text-background-50/60 text-2xl animate-bounce"></i>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-4 md:px-6 py-16">
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">
            精选推荐
          </h2>
          <Link
            to="/category/all"
            className="text-sm text-primary-500 hover:text-primary-600 flex items-center gap-1 cursor-pointer whitespace-nowrap"
          >
            查看全部 <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
          {featuredComics.map((comic) => (
            <ComicCard
              key={comic.id}
              {...comic}
              categoryName={categories.find((c) => c.slug === comic.category)?.name}
            />
          ))}
        </div>
      </section>

      <section className="bg-background-100/70 py-16">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-8">
            热门排行
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {trendingComics.slice(0, 8).map((comic, index) => (
              <Link
                key={comic.id}
                to={`/comic/${comic.id}`}
                className="flex items-center gap-4 p-4 rounded-lg bg-background-50 hover:bg-background-100 transition-colors cursor-pointer group"
                data-product-shop
              >
                <span
                  className={`text-2xl font-heading font-bold w-8 text-center ${
                    index < 3 ? 'text-primary-500' : 'text-foreground-300'
                  }`}
                >
                  {String(index + 1).padStart(2, '0')}
                </span>
                <div className="w-14 h-20 rounded-md overflow-hidden flex-shrink-0">
                  <img
                    src={comic.cover}
                    alt={comic.title}
                    className="w-full h-full object-cover"
                    width="800"
                    height="1100"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-label font-semibold text-foreground-900 truncate group-hover:text-primary-500 transition-colors">
                    {comic.title}
                  </h3>
                  <p className="text-xs text-foreground-500 mt-0.5">{comic.author}</p>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="flex items-center gap-0.5 text-xs text-accent-500">
                      <i className="ri-star-fill text-[10px]"></i> {comic.rating}
                    </span>
                    <span className="text-xs text-foreground-400">
                      共{comic.chapters}话
                    </span>
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <span className="text-xs text-foreground-400">
                    {formatViews(comic.views)} 阅读
                  </span>
                  <div className="mt-1">
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-label bg-primary-100 text-primary-700">
                      {comic.status}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-4 md:px-6 py-16">
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">
            分类浏览
          </h2>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 md:gap-4">
          {categories.filter((c) => c.slug !== 'all').map((cat) => {
            const count = comics.filter((c) => c.category === cat.slug).length;
            return (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="flex items-center gap-3 p-4 rounded-lg bg-background-100/70 hover:bg-primary-50 transition-colors cursor-pointer group"
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 group-hover:bg-primary-500 group-hover:text-background-50 transition-colors">
                  <i className={`${cat.icon} text-lg`}></i>
                </div>
                <div>
                  <h3 className="text-sm font-label font-semibold text-foreground-800">
                    {cat.name}
                  </h3>
                  <p className="text-xs text-foreground-400">{count} 部漫画</p>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      <section className="bg-background-100/70 py-16">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">
              最新更新
            </h2>
            <Link
              to="/category/all"
              className="text-sm text-primary-500 hover:text-primary-600 flex items-center gap-1 cursor-pointer whitespace-nowrap"
            >
              查看全部 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
            {latestComics.slice(0, 6).map((comic) => (
              <ComicCard
                key={comic.id}
                {...comic}
                categoryName={categories.find((c) => c.slug === comic.category)?.name}
              />
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-4 md:px-6 py-16">
        <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-8 text-center">
          为什么选择韩漫网？
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            {
              icon: 'ri-book-open-line',
              title: '海量资源',
              desc: '收录数千部优质韩国漫画，涵盖各种类型，总有一部适合你。',
            },
            {
              icon: 'ri-flashlight-line',
              title: '极速更新',
              desc: '每日同步更新最新章节，让追漫不再等待，精彩即刻呈现。',
            },
            {
              icon: 'ri-smartphone-line',
              title: '流畅阅读',
              desc: '适配多种设备，无论电脑还是手机，都能享受沉浸式阅读体验。',
            },
            {
              icon: 'ri-shield-check-line',
              title: '高清画质',
              desc: '所有漫画均为高清画质，精心优化每一页，还原韩漫精美细节。',
            },
          ].map((item, i) => (
            <div
              key={i}
              className="p-6 rounded-lg bg-background-100/70 text-center"
            >
              <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-4">
                <i className={`${item.icon} text-2xl`}></i>
              </div>
              <h3 className="text-base font-heading font-semibold text-foreground-900 mb-2">
                {item.title}
              </h3>
              <p className="text-sm text-foreground-500 leading-relaxed">
                {item.desc}
              </p>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}